import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { AutoComplete, Button, Input, InputProps, AutoCompleteProps } from 'ALIASED-antd';
import Grid from '@components/UI/Grid';
import { useDispatch } from 'react-redux';
import { RegistrationFormItem, type RegistrationFormItemProps } from '../RegistrationFormItem';
import { useAddressesAutoComplete } from '../utils/useSearchPostcode';
import { useRegistrationFormInstance } from '../useSelfRegistrationFormInstance';
import { getResolvedAddress } from '../../../../store/actions/patients';
import { useDebounce } from '@hooks/useDebounce';

export const AddressFieldset = ({
  inputProps,
  autoCompleteProps
}: {
  inputProps?: InputProps;
  autoCompleteProps?: AutoCompleteProps;
}) => {
  const { t } = useTranslation('common');
  const form = useRegistrationFormInstance();
  const dispatch = useDispatch();
  const [showAddressFields, setShowAddressFields] = useState(form.getFieldValue('addline1'));
  const { mutate: searchAddress, data } = useAddressesAutoComplete();
  const [isOpen, setIsOpen] = useState(false);

  const ADDRESS_FIELDS: (RegistrationFormItemProps & { autoComplete?: string })[] = [
    {
      label: 'Address Line 1',
      name: 'addline1',
      required: true,
      autoComplete: 'address-line1',
      rules: [
        {
          validator: (_, value) => {
            if (!value) {
              setShowAddressFields(true);
              return Promise.reject(new Error('Address Line 1 is required'));
            }
            return Promise.resolve();
          }
        }
      ]
    },
    { label: 'Address Line 2', name: 'addline2', autoComplete: 'address-line2' },
    { label: 'Address Line 3', name: 'addline3', autoComplete: 'address-line3' },
    { label: 'Town', name: 'addline4', autoComplete: 'address-level2' },
    { label: 'County', name: 'region', autoComplete: 'address-level1' },
    {
      label: 'Postcode',
      name: 'postcode',
      autoComplete: 'postal-code',
      required: true,
      rules: [
        {
          validator: (_, value) => {
            if (!value) {
              setShowAddressFields(true);
              return Promise.reject(new Error('Postcode is required'));
            }
            return Promise.resolve();
          }
        }
      ]
    }
  ];
  const handleSearch = useDebounce((value: string) => {
    searchAddress(value);
  }, 500);

  const onSelect = async (_: string, option: any) => {
    setShowAddressFields(true);
    const res = await dispatch(getResolvedAddress(option.id));

    if (!res?.result) {
      return;
    }

    const { line_1, line_2, line_3, post_town, county, postcode } = res.result;

    form.setFieldsValue({
      addline1: line_1,
      addline2: line_2,
      addline3: line_3,
      addline4: post_town,
      region: county,
      postcode
    });
  };

  return (
    <fieldset>
      <RegistrationFormItem
        label={t('Search for your address')}
        name="address-search"
        rules={[
          {
            validator: (_, value) => {
              if (value && data?.length === 0) {
                return Promise.reject(new Error('No addresses found'));
              }
              return Promise.resolve();
            }
          }
        ]}
        help={
          <>
            {form.getFieldValue('address-search') && data?.length === 0 && (
              <div style={{ color: '#d70015', marginBottom: '8px' }}>
                {t('No addresses found. Please try again or enter your address manually below.')}
              </div>
            )}
            {!showAddressFields && (
              <Button
                type="link"
                onClick={() => setShowAddressFields(true)}
                css={{ padding: 0 }}
                data-testId="show-address"
              >
                {t('Or enter your address manually')}
              </Button>
            )}
          </>
        }
      >
        <AutoComplete
          options={data?.map((address) => {
            return { value: address.suggestion, id: address.id, label: '' };
          })}
          onSelect={onSelect}
          onOpenChange={(open) => setIsOpen(open)}
          onSearch={handleSearch}
          {...autoCompleteProps}
        >
          <Input.Search
            data-testid="home-postcode"
            autoComplete="postal-code"
            aria-expanded={isOpen}
            enterButton
            aria-label={t('Search for your address by postcode')}
          />
        </AutoComplete>
      </RegistrationFormItem>

      <Grid columns={3}>
        {ADDRESS_FIELDS.map(({ name, autoComplete, ...rest }) => (
          <RegistrationFormItem key={String(name)} name={name} hidden={!showAddressFields} {...rest}>
            <Input autoComplete={autoComplete} {...inputProps} />
          </RegistrationFormItem>
        ))}
      </Grid>
    </fieldset>
  );
};
